<template>
  <div class="demo-tool">
    <div class="container">
      <h1>🔧 工具箱</h1>

      <div class="tabs">
        <button :class="{ active: currentTab === 'decode' }" @click="currentTab = 'decode'">文本解码</button>
        <button :class="{ active: currentTab === 'request' }" @click="currentTab = 'request'">请求调试</button>
        <button :class="{ active: currentTab === 'base64' }" @click="currentTab = 'base64'">Base64</button>
        <button :class="{ active: currentTab === 'unicode' }" @click="currentTab = 'unicode'">Unicode</button>
      </div>

      <!-- ===================== 文本解码 ===================== -->
      <div v-show="currentTab === 'decode'">
        <div class="input-group">
          <label>请输入要解码的文本内容：</label>
          <textarea v-model="decodeInput" placeholder="请输入需要解码的文本，支持RSA加密、Unicode编码等格式..."></textarea>
        </div>

        <div class="button-group">
          <button @click="decodeRSA">RSA解码</button>
          <button class="ghost" @click="clearDecode">清空</button>
        </div>

        <div v-if="decodeResult.show" class="result-area" :class="decodeResult.isError ? 'error' : 'success'">
          <div class="result-title">结果：</div>
          <div class="result-content">{{ decodeResult.text }}</div>
        </div>
      </div>

      <!-- ===================== Base64 ===================== -->
      <div v-show="currentTab === 'base64'">
        <div class="input-group">
          <label>请输入文本（编码）或 Base64 字符串（解码）：</label>
          <textarea v-model="base64Input" placeholder="支持中文，UTF-8 编解码..."></textarea>
        </div>

        <div class="button-group">
          <button @click="base64Encode">编码</button>
          <button @click="base64Decode">解码</button>
          <button class="ghost" @click="clearBase64">清空</button>
        </div>

        <div v-if="base64Result.show" class="result-area" :class="base64Result.isError ? 'error' : 'success'">
          <div class="result-title">结果：</div>
          <div class="result-content">{{ base64Result.text }}</div>
        </div>
      </div>

      <!-- ===================== Unicode ===================== -->
      <div v-show="currentTab === 'unicode'">
        <div class="input-group">
          <label>请输入文本（编码）或 \uXXXX 序列（解码）：</label>
          <textarea v-model="unicodeInput" placeholder="编码：非 ASCII 字符转为 \uXXXX；解码：将 \uXXXX 还原为字符..."></textarea>
        </div>

        <div class="button-group">
          <button @click="unicodeEncode">编码</button>
          <button @click="unicodeDecode">解码</button>
          <button class="ghost" @click="clearUnicode">清空</button>
        </div>

        <div v-if="unicodeResult.show" class="result-area" :class="unicodeResult.isError ? 'error' : 'success'">
          <div class="result-title">结果：</div>
          <div class="result-content">{{ unicodeResult.text }}</div>
        </div>
      </div>

      <!-- ===================== 请求调试 ===================== -->
      <div v-show="currentTab === 'request'">
        <div class="row">
          <div class="input-group method-col">
            <label>方法</label>
            <select v-model="req.method" class="field">
              <option value="POST">POST</option>
              <option value="GET">GET</option>
            </select>
          </div>
          <div class="input-group flex1">
            <label>BaseURL（URL 为绝对地址时忽略）</label>
            <input type="text" class="field" v-model="req.baseURL" placeholder="https://hzzksq.5535.cn/box">
          </div>
        </div>

        <div class="input-group">
          <label>请求地址（URL）</label>
          <input type="text" class="field" v-model="req.url" placeholder="例如：/publicH5/xxx 或 https://example.com/api">
        </div>

        <div class="input-group">
          <label>请求参数（JSON）</label>
          <textarea v-model="req.params" placeholder='例如：{"name":"tom"}'></textarea>
        </div>

        <div class="input-group">
          <label>自定义 Header（JSON，可选）</label>
          <textarea class="small" v-model="req.headers" placeholder='例如：{"X-Debug":"1"}'></textarea>
        </div>

        <div class="switch-row">
          <label class="switch"><input type="checkbox" v-model="req.encrypt"> 加密请求<span class="hint">（开=镜像 request.js，关=镜像 plaintextrequest.js）</span></label>
          <label class="switch"><input type="checkbox" v-model="req.withToken"> 携带 token<span class="hint">（wancms-users-token）</span></label>
        </div>

        <div class="button-group">
          <button @click="sendRequest" :disabled="loading">{{ loading ? '请求中...' : '发送请求' }}</button>
          <button class="ghost" @click="clearRequestResult">清空结果</button>
        </div>

        <!-- 结果分段 -->
        <div v-if="resp.show" class="result-area" :class="resp.isError ? 'error' : 'success'">
          <div class="overview">
            <span class="badge">{{ resp.method }}</span>
            <span class="badge" :class="statusClass">{{ resp.status }}</span>
            <span class="badge">{{ resp.time }}ms</span>
            <span class="overview-url">{{ resp.url }}</span>
          </div>

          <div class="seg">
            <div class="seg-title">已发送参数</div>
            <pre class="seg-body">{{ resp.sentParams }}</pre>
          </div>

          <div class="seg" v-if="resp.encrypted && resp.cipherBody">
            <div class="seg-title">加密 body（密文）</div>
            <pre class="seg-body">{{ resp.cipherBody }}</pre>
          </div>

          <div class="seg" v-if="resp.raw">
            <div class="seg-title">{{ resp.encrypted ? '原始响应（密文）' : '原始响应' }}</div>
            <pre class="seg-body">{{ resp.raw }}</pre>
          </div>

          <div class="seg" v-if="resp.encrypted && resp.decrypted">
            <div class="seg-title">解密后 JSON</div>
            <pre class="seg-body">{{ resp.decrypted }}</pre>
          </div>

          <div class="seg" v-if="resp.error">
            <div class="seg-title">错误</div>
            <pre class="seg-body">{{ resp.error }}</pre>
          </div>
        </div>

        <!-- 历史 -->
        <div class="history" v-if="history.length">
          <div class="history-head">
            <span>历史（最近 {{ history.length }} 条）</span>
            <button class="link" @click="clearHistory">清空历史</button>
          </div>
          <ul>
            <li v-for="(h, i) in history" :key="i">
              <div class="h-main">
                <span class="badge tiny">{{ h.method }}</span>
                <span class="badge tiny" :class="h.encrypt ? 'enc' : 'plain'">{{ h.encrypt ? '加密' : '明文' }}</span>
                <span class="badge tiny" :class="h.status >= 200 && h.status < 300 ? 'ok' : 'fail'">{{ h.status }}</span>
                <span class="h-url">{{ h.url }}</span>
              </div>
              <button class="link" @click="resend(h)">重发</button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import axios from 'axios'
import { encrypt, decrypt } from '@/utils/rsa'
import { getQueryString } from '@/utils/location'
import { getToken } from '@/utils/auth'

/* ---------------- 公共纯函数 ---------------- */
function decodeUnicodeString(text) {
  if (!text) return ''
  let replaced = text.replace(/\\u([0-9a-fA-F]{4})/g, (_, g1) => String.fromCharCode(parseInt(g1, 16)))
  try {
    replaced = decodeURIComponent(replaced)
  } catch (e) {
    // 忽略无效的 URL 编码
  }
  return replaced
}

function tryFormatAsJson(text) {
  if (typeof text !== 'string') return text
  try {
    return JSON.stringify(JSON.parse(text), null, 2)
  } catch (e) {
    return text
  }
}

const currentTab = ref('decode')

/* ---------------- 文本解码 ---------------- */
const decodeInput = ref('')
const decodeResult = reactive({ show: false, isError: false, text: '' })

function showDecode(text, isError = false) {
  decodeResult.text = text
  decodeResult.isError = isError
  decodeResult.show = true
}

const decodeRSA = () => {
  const text = decodeInput.value.trim()
  if (!text) {
    showDecode('请输入需要解码的文本', true)
    return
  }
  try {
    const result = decrypt(text)
    if (result == null || result === '') {
      showDecode('解码失败：结果为空或格式不正确', true)
      return
    }
    showDecode(tryFormatAsJson(decodeUnicodeString(result)), false)
  } catch (err) {
    showDecode('解码异常：' + (err && err.message ? err.message : String(err)), true)
  }
}

const clearDecode = () => {
  decodeInput.value = ''
  decodeResult.show = false
  decodeResult.text = ''
}

/* ---------------- Base64 编码/解码（UTF-8 安全） ---------------- */
const base64Input = ref('')
const base64Result = reactive({ show: false, isError: false, text: '' })

function showBase64(text, isError = false) {
  base64Result.text = text
  base64Result.isError = isError
  base64Result.show = true
}

const base64Encode = () => {
  const text = base64Input.value
  if (!text) {
    showBase64('请输入要编码的文本', true)
    return
  }
  try {
    showBase64(btoa(unescape(encodeURIComponent(text))), false)
  } catch (err) {
    showBase64('编码异常：' + (err && err.message ? err.message : String(err)), true)
  }
}

const base64Decode = () => {
  const text = base64Input.value.trim()
  if (!text) {
    showBase64('请输入要解码的 Base64 字符串', true)
    return
  }
  try {
    showBase64(decodeURIComponent(escape(atob(text))), false)
  } catch (err) {
    showBase64('解码异常：不是合法的 Base64 字符串', true)
  }
}

const clearBase64 = () => {
  base64Input.value = ''
  base64Result.show = false
  base64Result.text = ''
}

/* ---------------- Unicode 编码/解码 ---------------- */
const unicodeInput = ref('')
const unicodeResult = reactive({ show: false, isError: false, text: '' })

function showUnicode(text, isError = false) {
  unicodeResult.text = text
  unicodeResult.isError = isError
  unicodeResult.show = true
}

const unicodeEncode = () => {
  const text = unicodeInput.value
  if (!text) {
    showUnicode('请输入要编码的文本', true)
    return
  }
  // 非 ASCII 字符转为 \uXXXX，ASCII 保持原样
  const result = text.replace(/[\s\S]/g, (c) => {
    const code = c.charCodeAt(0)
    return code > 127 ? '\\u' + code.toString(16).padStart(4, '0') : c
  })
  showUnicode(result, false)
}

const unicodeDecode = () => {
  const text = unicodeInput.value
  if (!text) {
    showUnicode('请输入要解码的 \\uXXXX 序列', true)
    return
  }
  const result = text.replace(/\\u([0-9a-fA-F]{4})/g, (_, g1) => String.fromCharCode(parseInt(g1, 16)))
  showUnicode(result, false)
}

const clearUnicode = () => {
  unicodeInput.value = ''
  unicodeResult.show = false
  unicodeResult.text = ''
}

/* ---------------- 请求调试 ---------------- */
const req = reactive({
  method: 'POST',
  baseURL: process.env.VUE_APP_BASE_API || '',
  url: '',
  params: '',
  headers: '',
  encrypt: true,
  withToken: true,
})

const loading = ref(false)

const resp = reactive({
  show: false,
  isError: false,
  encrypted: false,
  method: '',
  url: '',
  status: '',
  time: 0,
  sentParams: '',
  cipherBody: '',
  raw: '',
  decrypted: '',
  error: '',
})

const statusClass = computed(() => {
  const s = Number(resp.status)
  return s >= 200 && s < 300 ? 'ok' : 'fail'
})

const HISTORY_KEY = 'demo_req_history'
const history = ref(loadHistory())

function loadHistory() {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY)) || []
  } catch (e) {
    return []
  }
}

function pushHistory(entry) {
  history.value.unshift(entry)
  if (history.value.length > 10) history.value = history.value.slice(0, 10)
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history.value))
}

function clearHistory() {
  history.value = []
  localStorage.removeItem(HISTORY_KEY)
}

function resend(h) {
  req.method = h.method
  req.url = h.url
  req.params = h.params
  req.encrypt = h.encrypt
  if (typeof h.withToken === 'boolean') req.withToken = h.withToken
  sendRequest()
}

function buildUrl() {
  const url = req.url.trim()
  const isAbsolute = /^(https?:)?\/\//i.test(url)
  if (isAbsolute) return url
  const base = req.baseURL.endsWith('/') ? req.baseURL.slice(0, -1) : req.baseURL
  const path = url.startsWith('/') ? url : '/' + url
  return base + path
}

const sendRequest = async () => {
  if (!req.url.trim()) {
    Object.assign(resp, { show: true, isError: true, error: '请输入请求地址（URL）', raw: '', decrypted: '', cipherBody: '', sentParams: '' })
    return
  }

  // 解析参数
  let dataObj = {}
  if (req.params && req.params.trim() !== '') {
    try {
      dataObj = JSON.parse(req.params)
    } catch (e) {
      Object.assign(resp, { show: true, isError: true, error: '参数需为合法 JSON：' + (e.message || e), raw: '', decrypted: '', cipherBody: '', sentParams: '' })
      return
    }
  }

  // 解析自定义 header
  let customHeaders = {}
  if (req.headers && req.headers.trim() !== '') {
    try {
      customHeaders = JSON.parse(req.headers)
    } catch (e) {
      Object.assign(resp, { show: true, isError: true, error: '自定义 Header 需为合法 JSON：' + (e.message || e), raw: '', decrypted: '', cipherBody: '', sentParams: '' })
      return
    }
  }

  // 注入项目参数（与 request.js / plaintextrequest.js 拦截器一致）
  dataObj.pt = 'zk'
  dataObj.key = getQueryString('key') || 'C7WglmY9'
  dataObj.appid = process.env.VUE_APP_BASE_APPID
  dataObj.versionCode = 1

  const headers = { ...customHeaders }
  if (req.withToken) {
    const token = getToken()
    if (token) headers['wancms-users-token'] = token
  }

  const finalUrl = buildUrl()
  const isGet = req.method === 'GET'
  const encrypted = req.encrypt && !isGet

  // 重置结果区
  Object.assign(resp, {
    show: true, isError: false, encrypted, method: req.method, url: finalUrl,
    status: '', time: 0, sentParams: JSON.stringify(dataObj, null, 2),
    cipherBody: '', raw: '', decrypted: '', error: '',
  })

  // 构造请求体
  let cipherBody = ''
  const config = {
    url: finalUrl,
    method: req.method,
    headers,
    timeout: 20000,
    responseType: 'text',
    transformResponse: [(d) => d], // 拿原始字符串
  }

  if (isGet) {
    config.params = dataObj
  } else if (encrypted) {
    cipherBody = encrypt(JSON.stringify(dataObj))
    resp.cipherBody = cipherBody
    config.data = cipherBody
  } else {
    config.data = dataObj // axios 序列化为 JSON
  }

  loading.value = true
  const start = Date.now()
  let status = ''
  try {
    const res = await axios(config)
    status = res.status
    const raw = typeof res.data === 'string' ? res.data : JSON.stringify(res.data)
    resp.raw = raw

    if (encrypted) {
      try {
        const plain = decrypt(raw)
        resp.decrypted = tryFormatAsJson(plain)
      } catch (e) {
        resp.isError = true
        resp.error = '响应解密失败（已保留原始密文）：' + (e.message || e)
      }
    } else {
      resp.raw = tryFormatAsJson(raw)
    }
  } catch (err) {
    resp.isError = true
    if (err.response) {
      status = err.response.status
      resp.raw = typeof err.response.data === 'string' ? err.response.data : JSON.stringify(err.response.data)
    } else {
      status = 'ERR'
    }
    resp.error = '请求失败：' + (err.message || String(err))
  } finally {
    loading.value = false
    resp.status = status
    resp.time = Date.now() - start
    pushHistory({
      method: req.method, url: finalUrl, encrypt: req.encrypt, withToken: req.withToken,
      params: req.params, status, time: resp.time,
    })
  }
}

const clearRequestResult = () => {
  resp.show = false
  Object.assign(resp, { raw: '', decrypted: '', cipherBody: '', sentParams: '', error: '' })
}
</script>

<!-- 本页仅 PC 使用：统一用 Px（postcss-px-to-viewport 只转换小写 px），避免按 750 设计稿被换算成 vw 而在 PC 上过大 -->
<style>
.demo-tool {
  font-family: Arial, sans-serif;
  max-width: 900Px;
  margin: 0 auto;
  padding: 16Px;
  background-color: #f5f5f5;
  min-height: 100vh;
  box-sizing: border-box;
}

.demo-tool .container {
  background: #fff;
  padding: 28Px 32Px;
  border-radius: 10Px;
  box-shadow: 0 2Px 12Px rgba(0, 0, 0, 0.08);
}

.demo-tool h1 {
  color: #333;
  text-align: center;
  margin: 0 0 24Px;
  font-size: 24Px;
}

.demo-tool .tabs {
  display: flex;
  justify-content: center;
  gap: 12Px;
  margin-bottom: 20Px;
}

.demo-tool .tabs button {
  background-color: #e0e0e0;
  color: #333;
}

.demo-tool .tabs button.active {
  background-color: #4caf50;
  color: #fff;
}

.demo-tool .input-group {
  margin-bottom: 16Px;
}

.demo-tool label {
  display: block;
  margin-bottom: 6Px;
  font-weight: bold;
  color: #555;
  font-size: 14Px;
}

.demo-tool textarea,
.demo-tool .field {
  width: 100%;
  padding: 9Px 12Px;
  border: 1Px solid #ddd;
  border-radius: 6Px;
  font-size: 14Px;
  box-sizing: border-box;
  background: #fff;
  color: #333;
}

.demo-tool textarea {
  height: 110Px;
  resize: vertical;
}

.demo-tool textarea.small {
  height: 64Px;
}

.demo-tool textarea:focus,
.demo-tool .field:focus {
  outline: none;
  border-color: #4caf50;
}

.demo-tool .row {
  display: flex;
  gap: 16Px;
}

.demo-tool .method-col {
  width: 120Px;
  flex: none;
}

.demo-tool .flex1 {
  flex: 1;
}

.demo-tool .switch-row {
  display: flex;
  flex-wrap: wrap;
  gap: 24Px;
  margin-bottom: 16Px;
}

.demo-tool .switch {
  display: flex;
  align-items: center;
  gap: 6Px;
  font-weight: normal;
  font-size: 14Px;
  color: #444;
  margin: 0;
  cursor: pointer;
}

.demo-tool .switch input {
  width: 16Px;
  height: 16Px;
}

.demo-tool .switch .hint {
  color: #999;
  font-size: 12Px;
}

.demo-tool .button-group {
  text-align: center;
  margin: 20Px 0;
}

.demo-tool button {
  background-color: #4caf50;
  color: #fff;
  padding: 9Px 22Px;
  border: none;
  border-radius: 6Px;
  cursor: pointer;
  font-size: 15Px;
  margin: 0 8Px;
  transition: background-color 0.3s;
}

.demo-tool button:hover {
  background-color: #45a049;
}

.demo-tool button.ghost {
  background-color: #e0e0e0;
  color: #333;
}

.demo-tool button.ghost:hover {
  background-color: #d2d2d2;
}

.demo-tool button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.demo-tool .result-area {
  margin-top: 16Px;
  padding: 16Px;
  background-color: #f9f9f9;
  border-radius: 6Px;
  border-left: 4Px solid #4caf50;
}

.demo-tool .result-title {
  font-weight: bold;
  color: #333;
  margin-bottom: 10Px;
  font-size: 14Px;
}

.demo-tool .result-content,
.demo-tool .seg-body {
  background: #fff;
  padding: 12Px;
  border-radius: 4Px;
  border: 1Px solid #ddd;
  white-space: pre-wrap;
  word-break: break-all;
  min-height: 32Px;
  margin: 0;
  font-size: 13Px;
  line-height: 1.5;
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
}

.demo-tool .overview {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8Px;
  margin-bottom: 14Px;
}

.demo-tool .overview-url {
  color: #555;
  font-size: 12Px;
  word-break: break-all;
}

.demo-tool .badge {
  display: inline-block;
  padding: 2Px 10Px;
  border-radius: 4Px;
  background: #607d8b;
  color: #fff;
  font-size: 12Px;
}

.demo-tool .badge.tiny {
  padding: 1Px 8Px;
  font-size: 11Px;
}

.demo-tool .badge.ok {
  background: #2e7d32;
}

.demo-tool .badge.fail {
  background: #d32f2f;
}

.demo-tool .badge.enc {
  background: #6a1b9a;
}

.demo-tool .badge.plain {
  background: #ef6c00;
}

.demo-tool .seg {
  margin-top: 12Px;
}

.demo-tool .seg-title {
  font-weight: bold;
  color: #444;
  margin-bottom: 5Px;
  font-size: 13Px;
}

.demo-tool .error {
  background-color: #ffebee;
  border-left-color: #d32f2f;
}

.demo-tool .success {
  background-color: #e8f5e8;
  border-left-color: #2e7d32;
}

.demo-tool .history {
  margin-top: 24Px;
  border-top: 1Px dashed #ddd;
  padding-top: 14Px;
}

.demo-tool .history-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  color: #555;
  font-size: 13Px;
  margin-bottom: 8Px;
}

.demo-tool .history ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.demo-tool .history li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12Px;
  padding: 9Px 0;
  border-bottom: 1Px solid #eee;
}

.demo-tool .h-main {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6Px;
  min-width: 0;
}

.demo-tool .h-url {
  color: #555;
  font-size: 12Px;
  word-break: break-all;
}

.demo-tool .link {
  background: none;
  color: #4caf50;
  padding: 0;
  margin: 0;
  font-size: 13Px;
  flex: none;
}

.demo-tool .link:hover {
  background: none;
  text-decoration: underline;
}
</style>
